import React, { useEffect, useRef } from 'react';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import { useKeycloak } from '@react-keycloak/web';

const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { initialized, authenticated, login } = useUnifiedAuth();
  const { keycloak } = useKeycloak();
  const loginTriggered = useRef(false);
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Automatically trigger login when user is not authenticated
  useEffect(() => {
    if (initialized && !authenticated && !loginTriggered.current) {
      loginTriggered.current = true;
      console.log('🔐 Auto-triggering login since user is not authenticated');
      
      // Try the login() method first
      login();
      
      // If login() doesn't redirect within 3 seconds, force redirect
      redirectTimer.current = setTimeout(async () => {
        if (!authenticated && keycloak) {
          console.log('🔐 Login method did not redirect, forcing Keycloak login URL redirect');
          const loginUrl = await keycloak.createLoginUrl({
            redirectUri: window.location.origin
          });
          window.location.href = loginUrl;
        }
      }, 3000);
    }

    return () => {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
      }
    };
  }, [initialized, authenticated, login, keycloak]);

  if (!initialized) {
    return (
      <div className="min-h-screen bg-cockpitbg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cockpitblue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cockpitblue text-lg font-semibold">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-cockpitbg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cockpitblue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-title mb-4">Redirecting to Login</h1>
          <p className="text-gray-600 mb-6">Redirecting to Keycloak login page...</p>
          <p className="text-sm text-gray-500 mb-4">If you're not redirected automatically:</p>
          <button
            onClick={async () => {
              if (keycloak) {
                const loginUrl = await keycloak.createLoginUrl({
                  redirectUri: window.location.origin
                });
                window.location.href = loginUrl;
              } else {
                login();
              }
            }}
            className="bg-cockpitblue text-white px-6 py-3 rounded-lg font-semibold hover:bg-cockpitblue-dark transition"
          >
            Click here to Login
          </button>
        </div>
      </div>
    );
  }

  // Clear timer if user becomes authenticated
  useEffect(() => {
    if (authenticated && redirectTimer.current) {
      clearTimeout(redirectTimer.current);
    }
  }, [authenticated]);

  return <>{children}</>;
};

export default AuthGuard;
